<template>
  <div>
    <mdb-container class="gp-has-header" v-if="loggedIn || settings.groupsMayBePublic">
      <div v-if="translationMode">
        <translate-section :items="translateItems" category="page" sub-category="explore"/>
      </div>

      <mdb-card-header
        v-if="loggedIn && userDetails && userDetails.groups.length==0 && userDetails.visited.length == 0 && waited"
        class="mb-2 warning-color text-center top-alert-body">
        <mdb-icon color="danger" icon="hand-paper"/>
        {{ $t('page.explore.oneMoreStep') }}
      </mdb-card-header>

      <mdb-card class="mb-4">
        <mdb-card-header class="text-center secondary-color text-white">
          <h4>
            {{ $t('page.explore.findAGroup') }}
            <translate-modal v-if="translationMode" category="page" sub-category="explore" name="findAGroup"/>
          </h4>
        </mdb-card-header>
        <mdb-card-body class="text-left">
          <gp-select v-if="searchCountries.length && listLoaded"
                     :dataObj="searchParams"
                     :possible-values="searchCountries"
                     :required="false" icon="globe"
                     :label="capitalize($tc('common.label.country',1))"
                     propertyName="country"/>
          <translate-modal v-if="translationMode" category="common" sub-category="label" name="country"/>

          <mdb-input v-if="searchParams.country && searchParams.country != 'gbl'" id="ignore-global" v-model="ignoreGlobal" label="Don't show online/global groups" type="checkbox"/>

          <mdb-input v-model="searchParams.query" fal
                     icon="search"
                     :label="capitalize($t('common.label.search'))"
                     :placeholder="$t('page.explore.searchPlaceholder')" type="text"
                     @keydown.native="handleKeyDown"
          >
            <translate-modal slot="append" v-if="translationMode" category="common" sub-category="label" name="search"/>
            <translate-modal slot="append" v-if="translationMode" category="page" sub-category="explore"
                             name="searchPlaceholder"/>
          </mdb-input>

          <div v-if="allTags && allTags.length>0 && !showCategoryFilters">
            <a href="" @click.prevent="showCategoryFilters=true">{{ $t("page.explore.showCategoryFilters") }}</a>
            <translate-modal v-if="translationMode" category="page" sub-category="explore" name="showCategoryFilters"/>
          </div>

          <div v-if="showCategoryFilters">
            <h5>
              <mdb-icon fal icon="filter"/>
              {{ capitalize($tc('common.label.filter', 2)) }}:
              <translate-modal v-if="translationMode" category="common" sub-category="label" name="filter"/>
            </h5>

            <mdb-row>
              <mdb-col sm="12" md="6" lg="4" v-for="tag in allTags" :key="tag.id">
                <mdb-input :id="'tag-'+tag.id" v-model="tag.selected" :label="tag.name" type="checkbox"/>
              </mdb-col>
            </mdb-row>
            <div>
              <mdb-btn rounded size="sm" @click="showCategoryFilters=false">{{ $t('page.explore.hideFilters') }}
              </mdb-btn>
              <translate-modal v-if="translationMode" category="page" sub-category="explore" name="hideFilters"/>
            </div>
          </div>

          <mdb-btn icon="search" @click="loadData(false)">{{ capitalize($t('common.button.search')) }}</mdb-btn>
          <translate-modal v-if="translationMode" category="common" sub-category="button" name="search"/>

          <p class="small">
            {{ $t('page.explore.privateGroupNote') }}
            <translate-modal v-if="translationMode" category="page" sub-category="explore" name="privateGroupNote"/>
          </p>
          <p class="small" v-if="settings.useGroupTypeLabels">{{ $t("page.explore.groupsAreCategorized") }}
            <span v-for="(type,i) in settings.groupTypes" :key="i" >
              <span :class="type.badgeClass">
              {{type.name}}</span>{{ ( i < settings.groupTypes.length-1) ? ',' : ''}}{{ (i==settings.groupTypes.length -2) ? ' and' : ''}}&nbsp;
            </span>
          </p>
        </mdb-card-body>
      </mdb-card>

      <mdb-row v-if="groups.length==0 && didSearch">
        <mdb-col md="12">
          <mdb-card class="mb-4 text-left">
            <mdb-card-header class="primary-color text-white">
              {{ capitalize($t("common.label.result", 2)) }}
            </mdb-card-header>
            <mdb-card-body>
              <div class="text-info">
                {{ $t("page.explore.noGroupsFound") }}
              </div>
            </mdb-card-body>
          </mdb-card>
        </mdb-col>
      </mdb-row>

      <mdb-row v-if="groups.length>0">
        <div v-for="(g,i) in groups" :key="i" class="col-lg-3 col-md-12">
          <a href="" @click.prevent="showGroup(g)">
            <mdb-card class="mb-4" ecommerce narrow style="min-height: 400px;">
              <mdb-view cascade hover>
                <mdb-card-image :alt="g.name + ' avatar'" :src="g.avatar"
                                style="background-color: #ffffff;"
                                top
                                waves
                ></mdb-card-image>
                <div class="plan-badge" v-if="!g.isOtherTenant && settings.useGroupTypeLabels && g.groupType">
                  <mdb-badge :color="g.groupType.badgeClass">{{ g.groupType.name }}</mdb-badge>
                </div>
                <div class="plan-badge" v-if="g.isOtherTenant">
                  <mdb-badge color="warning">On sister site</mdb-badge>
                </div>
                <mdb-mask flex-center overlay="white-slight" waves></mdb-mask>

              </mdb-view>
              <mdb-card-body cascade>
                <div>{{ g.name }}</div>
                <div>{{ g.city ? g.city : '&nbsp;' }}</div>
                <div style="position: absolute; bottom: 0; right: 0;">
                  <mdb-badge>{{ g.members }} {{ g.memberLabel }}</mdb-badge>
                </div>
              </mdb-card-body>
            </mdb-card>
          </a>
        </div>
      </mdb-row>
      <mdb-btn v-if="totalGroups > groups.length && groups.length!=0" @click="loadData(true)">
        {{ capitalize($t("common.button.loadMore")) }}
      </mdb-btn>

    </mdb-container>

  </div>

</template>

<script>
import api from '../services/groupsPlaceApi.js'
import groupCard from './group/Card'
import gpSelect from "./common/GPSelect";
import translateModal from "./common/TranslateModal";
import translateSection from "./common/TranslateSection";

import {
  mdbBadge,
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardGroup,
  mdbCardHeader,
  mdbCardImage,
  mdbCardText,
  mdbCardTitle,
  mdbChip,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbRow,
  mdbView
} from 'mdbvue';

import mdbVectorMap from "mdbvue-vector-map";

export default {
  name: 'Pricing',
  components: {
    mdbContainer,
    mdbCard,
    mdbCardHeader,
    mdbCardGroup,
    mdbCardBody,
    mdbCardTitle,
    mdbCardText,
    mdbChip,
    mdbRow,
    mdbCol,
    mdbMask,
    mdbCardImage,
    mdbView,
    mdbBadge,
    mdbBtn,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbInput,
    mdbIcon,
    gpSelect,
    groupCard,
    mdbVectorMap,
    translateModal,
    translateSection
  },
  metaInfo() {
    return {
      link: [
        {
          rel: "canonical",
          href: this.settings.staticSiteUrl + "/explore"
        }
      ]
    }
  },
  data() {
    return {
      groups: [],
      offset: 0,
      totalGroups: 0,
      currentGroup: null,
      isTyping: false,
      mode: "filter",
      countries: [],
      ignoreGlobal: false,
      searchCountries: [],
      searchParams: {country: null, query: null},
      currentCity: null,
      allTags: [],
      countryModal: false,
      mapOptions: {},
      listLoaded: false,
      showCategoryFilters: false,
      didSearch: false,
      waited: false,
      translateItems: []
      /*translateItems: [
        'common.groupType.free',
        'common.groupType.premium',
        'common.groupType.premiumWithFreeTrial'
      ]*/
    }
  },
  computed: {
    settings() {
      return this.$store.state.settings
    },
    loggedIn() {
      return this.$store.state.loggedIn
    },
    translationMode() {
      return this.$store.state.translationMode
    },
    userDetails() {
      return this.$store.state.userDetails
    }
  },
  watch: {
    isTyping: function (value) {
      if (!value) {
        this.loadData(false)
      }
    }
  },
  mounted() {
    if (this.$route.query.country) {
      this.searchParams.country = this.$route.query.country
    }
    this.loadCountries()
    this.loadAllTags()
    this.loadData(false)
    setTimeout(() => {
      this.waited = true
    }, 3000)
    document.title = this.$t("page.explore.pageTitle")
  },
  methods: {
    countrySelector() {
      this.countryModal = true
    },
    handleKeyDown(e) {
      if (e.keyCode == 13) this.loadData(false)
    },
    changeCheckbox(x, y) {
      console.log("Changed check ", x, y)
    },
    pubHome() {
      window.open(this.settings.staticSiteUrl)
    },
    loadAllTags: function () {
      api.get("/public/allTags?onlyUsed=Y").then(
        (results) => {
          this.allTags = results.data
          if (this.allTags && this.allTags.length > 0) {
            for (let i = 0; i < this.allTags.length; i++) {
              this.allTags[i].selected = false
            }
          }
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        })
    },
    showGroup(group) {
      if (group.isOtherTenant) {
        window.open(group.url, "_blank")
      }
      else this.$router.push(group.url)
    },
    loadData(loadMore) {
      if (!loadMore) {
        this.groups = []
        this.totalGroups = 0
      }
      this.$store.dispatch("setExecuting", false)
      let url = "/public/explore/"
      let params = ""
      if (this.searchParams.query) params += "q=" + this.searchParams.query
      if (this.searchParams.country) {
        if (params) params += "&"
        params += "country=" + this.searchParams.country
        if (this.ignoreGlobal && this.searchParams.country != 'gbl') {
          params += "&ignoreGlobal=Y"
        }
      }
      if (this.showCategoryFilters) {
        for (let i = 0; i < this.allTags.length; i++) {
          if (this.allTags[i].selected) {
            if (params) params += "&"
            params += "tag=" + this.allTags[i].id
          }
        }
      }
      if (loadMore) {
        if (params) params += "&"
        params += "offset=" + this.groups.length
      }
      if (params) url += "?" + params
      this.$store.dispatch("setExecuting", true)
      api.get(url).then(
        (results) => {
          this.didSearch = true
          if (results.data.country) this.searchParams.country = results.data.country
          this.listLoaded = true
          if (loadMore) {
            let rows = results.data.groups
            for (let i = 0; i < rows.length; i++) {
              this.groups.push(rows[i])
            }
          } else {
            this.groups = results.data.groups
            this.totalGroups = results.data.totalGroups
          }
        },
        (error) => {
          this.$notify.error({message: "Error fetching page.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    loadCountries() {
      api.get("/public/countryList").then(
        (results) => {
          this.countries = results.data
          for (const country of this.countries) {
            this.searchCountries.push({text: country.name, value: country.alpha3Code.toLowerCase()})
          }
        },
        (error) => {
          this.$notify.error({message: "Error fetching countries.", timeOut: 3000})
        }).then(() => {
      })
    }
  }

};
</script>
<style scoped>
.plan-badge {
  position: absolute;
  top: 0;
  right: 0;
}
</style>
